import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Link, Route, Switch, Redirect, useHistory } from "react-router-dom";
import { Button, Col, Container, Modal, Nav, NavDropdown, Row, Spinner } from "react-bootstrap";
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import Alert from "react-bootstrap/Alert";
import { MdSearch } from "react-icons/md";

import SaluteCard from "./Common/HomeWidgets/Widgets/Salute/SaluteCard";
import LabResult from "./LabResult";
import UserResult from "./UserResult/UserResult";
import UserRequest from "./UserRequest/UserRequest";
import LabRequest from "./LabRequest";
import ReportOrders from "./ReportOrders";
import MessageView from "./MessageView";
import Profile_deprecated from "./Profile_deprecated";
import Profile, { profileRedirectReason } from "./Profile";
import BookingList from "./BookingList";
import BookingCalendar from "./BookingCalendar";
import BookingDetails from "./BookingDetails";
import KitDetailsPage from "./KitDetailsPage";
import PatientProfile from "./PatientProfileV2/PatientProfilePage";
import PatientSearchPage from "./PatientSearchPage";
import CollectPayment from "./CollectPayment";
import Inbox from "./MessageIndexInbox";
import ActiveCases from "./HomeTable/ActiveCases";
import WidgetRow from "./Common/HomeWidgets/WidgetRow/WidgetRow";
import Dashboard from "./Dashboard";
import ManageLabOrder from "./MangeLabOrder";
import ShippingManifest from "./ShippingManifest";
import Unauthorized from "./Unauthorized";
import AuthWrapper from "./Auth/AuthWrapper";
import PrivateRoute from "./PrivateRoute";
import AdminDashboard from "./SuperAdminPanel/Dashboard";

import ApiService from "../ApiService";
import { Auth } from "@aws-amplify/auth";
import { clinicianIdFromRefreshAuth, getUserRoles, ROLE_NAME, CLINICIAN_STATUS } from "../util/authUtil";
import { parseAxiosError } from "../util/parserHelper";
import url, { NAV_LABELS } from "../util/routeURL";
import removeClass from "../util/RemoveCSS";
import logo from "../assets/animated_logo1.gif";

// Alert component for snackbars
function SnackStyleAlert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

// Modal to prompt users for license info if needed
function OnboardingModalOne({ show, onHide }) {
  const message = "Before getting started, you'll need to enter your clinician licensing information.";
  return (
    <Modal show={show} onHide={onHide} size="lg" aria-labelledby="contained-modal-title-vcenter" centered backdrop="static" keyboard={false}>
      <Modal.Body className="onboard-modal">
        <h2 className="pb-20">Welcome to TBD!</h2>
        <p className="pb-20">{message}</p>
        <Link to={`/profile?reason=${profileRedirectReason.noLicense.slug}`} rel="noopener noreferrer">
          <Button className="button-green">Enter License Information</Button>
        </Link>
      </Modal.Body>
    </Modal>
  );
}

// A simple Home component that renders the dashboard along with snackbars and banners.
const Home = ({ userProfile, snackOpen, snackText, snackSeverity, handleSnackClose, bannerProps, isShowBanner }) => {
  if (!userProfile) {
    return (
      <Row className="justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Row>
    );
  }

  return (
    <Container fluid>
      <Snackbar open={snackOpen} autoHideDuration={6000} onClose={handleSnackClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <SnackStyleAlert onClose={handleSnackClose} severity={snackSeverity}>
          {snackText}
        </SnackStyleAlert>
      </Snackbar>
      {isShowBanner && (
        <Alert variant={bannerProps.variant}>
          {bannerProps.heading && <Alert.Heading>{bannerProps.heading}</Alert.Heading>}
          <p>{bannerProps.message}</p>
        </Alert>
      )}
      <Dashboard />
      {/* Show the onboarding modal if the user has no license */}
      {userProfile.hasNoLicense && <OnboardingModalOne show={true} onHide={() => {}} />}
    </Container>
  );
};

const Navigation = () => {
  const history = useHistory();
  const [clinicianId, setClinicianId] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackText, setSnackText] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("");
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState("/");
  const [bannerProps, setBannerProps] = useState({});
  const [isShowBanner, setIsShowBanner] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [preReqMet, setPreReqMet] = useState(false);

  // Remove any unwanted CSS classes only once when mounting.
  useEffect(() => {
    removeClass();
  }, []);

  // Memoized snack handlers
  const handleSnackOpen = useCallback((text, severity) => {
    setSnackSeverity(severity);
    setSnackText(text);
    setSnackOpen(true);
  }, []);

  const handleSnackClose = useCallback(() => {
    setSnackOpen(false);
  }, []);

  // Memoized logout function.
  const logout = useCallback(async () => {
    try {
      await Auth.signOut();
    } catch (err) {
      console.error(err);
    } finally {
      localStorage.clear();
      window.location.reload();
    }
  }, []);

  // Initialize authentication, load profile, and user roles.
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        const id = await clinicianIdFromRefreshAuth();
        setClinicianId(id);
        const profileResponse = await ApiService.getClinicianProfile();
        if (profileResponse?.data?.status === CLINICIAN_STATUS.INACTIVE) {
          handleSnackOpen("Your account is disabled, please contact support", "error");
          logout();
          return;
        }
        setUserProfile(profileResponse.data);
        const roles = await getUserRoles();
        setUserRoles(roles);
      } catch (err) {
        console.error(err);
        handleSnackOpen(`Initialization failed: ${err?.message ?? "unknown cause"}`, "error");
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [handleSnackOpen]);

  // Update UI state based on profile requirements.
  useEffect(() => {
    if (!userProfile) {
      setBannerProps({
        heading: "Clinician Profile not found",
        message: "Please contact us to resolve this issue",
        variant: "danger"
      });
      setIsShowBanner(true);
      return;
    }

    if (!userRoles?.length) {
      setBannerProps({
        heading: "You have not been assigned any roles",
        message: "Please contact us to resolve this issue",
        variant: "danger"
      });
      setIsShowBanner(true);
      return;
    }

    setPreReqMet(true);
    // setBannerProps({
    //   heading: "Access restricted",
    //   message: `Your profile is missing license info, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
    //   variant: "danger"
    // });
    // return

    if (userRoles.includes(ROLE_NAME.CLINICIAN)) {
      if (userProfile.hasNoLicense) {
        setBannerProps({
          heading: "No License have been added",
          message: `Your profile is missing license info, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
          variant: "danger"
        });
        setIsShowBanner(true);
        // Do not redirect; the onboarding modal will prompt for license info.
        return;
      }

      if (!userProfile.hasCalendar) {
        setBannerProps({
          heading: "Calendar link missing",
          message: `Your profile is missing calendar link, please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update`,
          variant: "danger"
        });
        setIsShowBanner(true);
        setShouldRedirect(true);
        setRedirectPath(`/profile?reason=${profileRedirectReason.invalidOrNoCalendar.slug}`);
        return;
      }

      if (userProfile?.hasExpiredOrInValidLicense) {
        const invalidStates = Array.isArray(userProfile?.inValidLicenseStates) ? userProfile.inValidLicenseStates.filter(Boolean) : [];

        const invalidStatesDisplay = invalidStates.length ? ` for the following state(s): ${invalidStates.join(", ")}` : "";

        setBannerProps({
          heading: "Invalid or expired licenses",
          message: `Your profile has invalid or expired licenses${invalidStatesDisplay}. Please go to ${NAV_LABELS.ACCOUNT} > ${NAV_LABELS.MANAGE_PROFILE} to update.`,
          variant: "danger"
        });

        setIsShowBanner(true);
        // setShouldRedirect(true);
        // setRedirectPath(`/profile?reason=${profileRedirectReason.expiredOrInvalidLicense.slug}`);
        return;
      }
    }

    const restrictedRoles = [ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT, ROLE_NAME.AUDITOR_READ_ONLY];
    const hasOnlyRestrictedRole = Array.isArray(userRoles) && userRoles.length > 0 &&
      userRoles.every(role => restrictedRoles.includes(role));

    if (hasOnlyRestrictedRole) {
      setBannerProps({
        heading: `Access is restricted`,
        message: "You do not have access to clinical operations in the EMR Dashboard",
        variant: "warning"
      });
      setIsShowBanner(true);
      setPreReqMet(true);
      return;
    }

    if (userRoles.includes(ROLE_NAME.SUPER_ADMIN)) {
      setBannerProps({
        heading: "WELCOME, SUPER ADMIN",
        message: "You have full access to all features",
        variant: "success"
      });
      setPreReqMet(true);
      return;
    }

    // All prerequisites met.
    setPreReqMet(true);
  }, [userProfile, userRoles]);

  if (loading) {
    return (
      <Row className="justify-content-center mt-5">
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Row>
    );
  }

  return (
    <Router>
      {/* If prerequisites aren’t met, perform redirection */}
      {shouldRedirect && <Redirect to={redirectPath} />}
      <Nav defaultActiveKey="/">
        <Nav.Item>
          <img className="nav-link logo" src={logo} alt={NAV_LABELS.LOGO_ALT} />
        </Nav.Item>
        <Nav.Item>
          <Link className="nav-link" to="/">
            {NAV_LABELS.HOME}
          </Link>
        </Nav.Item>
        <Nav.Item>
          <Link className="nav-link" to="/inbox">
            {NAV_LABELS.MESSAGES}
          </Link>
        </Nav.Item>
        <NavDropdown title={NAV_LABELS.APPOINTMENTS} id="appointments-dropdown">
          <NavDropdown.Item as={Link} to="/BookingList">
            {NAV_LABELS.FIND_APPOINTMENTS}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/BookingCalendar">
            {NAV_LABELS.CALENDAR}
          </NavDropdown.Item>
        </NavDropdown>
        <NavDropdown title={NAV_LABELS.LAB} id="lab-dropdown">
          <NavDropdown.Item as={Link} to="/request">
            {NAV_LABELS.LAB_REQUESTS}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/result">
            {NAV_LABELS.LAB_RESULTS}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/manage-lab-order">
            {NAV_LABELS.MANAGE_LAB_ORDER}
          </NavDropdown.Item>
          <NavDropdown.Item as={Link} to="/shipping-manifest">
            {NAV_LABELS.SHIPPING_MANIFEST}
          </NavDropdown.Item>
        </NavDropdown>
        <NavDropdown title={NAV_LABELS.SEARCH} id="search-dropdown">
          <NavDropdown.Item as={Link} to="/SearchPatient">
            {NAV_LABELS.PATIENT_SEARCH}
          </NavDropdown.Item>
        </NavDropdown>
        <NavDropdown title={NAV_LABELS.ACCOUNT} id="account-dropdown">
          {!loading && userRoles.includes(ROLE_NAME.SUPER_ADMIN) && (
            <NavDropdown.Item as={Link} to="/super-admin">
              {NAV_LABELS.SUPER_ADMIN}
            </NavDropdown.Item>
          )}
          <NavDropdown.Divider />
          <NavDropdown.Item as={Link} to="/profile">
            {NAV_LABELS.MANAGE_PROFILE}
          </NavDropdown.Item>
          <NavDropdown.Divider />
          <NavDropdown.Item as={Link} onClick={logout}>
            {NAV_LABELS.LOGOUT}
          </NavDropdown.Item>
          <NavDropdown.Divider />
        </NavDropdown>
      </Nav>

      <Switch>
        <AuthWrapper>
          {preReqMet && (
            <>
              <PrivateRoute exact path={url.admin} component={AdminDashboard} allowedRoles={[ROLE_NAME.SUPER_ADMIN]} />
              <PrivateRoute
                exact
                path={url.inbox}
                component={Inbox}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path={url.inboxItem}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/MessageDetails"
                component={MessageView}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/request"
                component={LabRequest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/manage-lab-order"
                component={ManageLabOrder}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/shipping-manifest"
                component={ShippingManifest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/result"
                component={LabResult}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/result/:kitid"
                component={UserResult}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/userrequest/:kitid"
                component={UserRequest}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/report/orders"
                component={ReportOrders}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingList"
                component={BookingList}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingCalendar"
                component={BookingCalendar}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/BookingDetails"
                component={BookingDetails}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/KitDetails"
                component={KitDetailsPage}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/SearchPatient"
                component={PatientSearchPage}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/PatientProfile"
                component={PatientProfile}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
              <PrivateRoute
                exact
                path="/Payment"
                component={CollectPayment}
                allowedRoles={[ROLE_NAME.CLINICIAN, ROLE_NAME.AUDITOR_READ_ONLY, ROLE_NAME.SUPER_ADMIN, ROLE_NAME.STAFF, ROLE_NAME.OPERATION_SUPPORT]}
              />
            </>
          )}
          <Route path="/license" component={() => <Profile isGoLicenseView />} />
          <Route path="/profile" component={Profile} />
          <Route path="/calendar" component={() => <Profile isGoCalendarView />} />
          <Route exact path="/">
            <Home
              userProfile={userProfile}
              snackOpen={snackOpen}
              snackText={snackText}
              snackSeverity={snackSeverity}
              handleSnackClose={handleSnackClose}
              bannerProps={bannerProps}
              isShowBanner={isShowBanner}
            />
          </Route>
          <Route path="/401-page">
            <Unauthorized userProfile={userProfile} />
          </Route>
        </AuthWrapper>
      </Switch>
    </Router>
  );
};

export default Navigation;
